/**
 * GlobalDashboardHeader
 * Shared top-of-page component for all onPointBI dashboard views.
 * Layout: Two stacked rows.
 *   Row 1: Today's Market (left ~70%) | Business Valuation (right ~30%) - 56px
 *   Row 2: News (full width) - 40px
 * Rendered as the first element on: Executive Summary, Profits and Cash Flow,
 * Income and Expenses, Liabilities and Equities, Sales,
 * Expenses by Category, Ratios, Conversion Cycles.
 * Data: reuses existing API endpoints (/market/tickers, /valuation/calculate, /market/news).
 * Accessibility: WCAG AA compliant. aria-labels on all sections.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { TrendingUp, Info, X } from "lucide-react";
import api from "../../utils/api";
import "./GlobalDashboardHeader.css";

const VALUATION_DISCLAIMER_PARAGRAPHS = [
  "Business Valuation Estimate (Informational Only): The business valuation displayed on this dashboard is an automated, preliminary estimate based on limited information and general assumptions. It is provided solely for informational purposes as a ballpark reference and is not a formal appraisal, certified valuation, or opinion of value. The estimate may be incomplete, inaccurate, or may differ materially from a valuation prepared by a qualified professional.",
  "You should not rely on this estimate to make (or refrain from making) any decision regarding the sale, purchase, merger, financing, investment, tax, legal, or other transaction involving your business. You should obtain an independent valuation and appropriate professional advice before making any such decisions.",
  "OnPoint Business Intelligence, Inc. provides this estimate \"as is\" and makes no representations or warranties regarding its accuracy, completeness, or fitness for any particular purpose. To the fullest extent permitted by applicable law, OnPoint Business Intelligence, Inc. disclaims any and all liability for any loss, damages, or claims arising from the use of, access to, or reliance on this estimate.",
  "By clicking the Business Valuation tab (or continuing to view this information), you acknowledge and agree to the terms above.",
];

export const GlobalDashboardHeader = () => {
  const { user } = useSelector((s) => s.auth);
  const [tickers, setTickers] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [news, setNews] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const industry = user?.industry || "consulting";

  useEffect(() => {
    const load = async () => {
      try {
        const [tickerRes, valRes, newsRes] = await Promise.all([
          api.get("/market/tickers").catch(() => ({ data: { tickers: [] } })),
          api.get("/valuation/calculate").catch(() => ({ data: null })),
          api.get(`/market/news?industry=${encodeURIComponent(industry)}`).catch(() => ({ data: { news: [] } })),
        ]);
        setTickers(tickerRes.data?.tickers || []);
        setValuation(valRes.data);
        setNews(newsRes.data?.news || []);
      } catch { /* fail silently */ }
    };
    load();
    const interval = setInterval(async () => {
      try {
        const r = await api.get("/market/tickers");
        setTickers(r.data?.tickers || []);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [industry]);

  const fmtCurrency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

  const healthScore = valuation?.health_score || 0;

  return (
    <div data-testid="global-dashboard-header" className="gdh-container">
      {/* ROW 1: Today's Market + Business Valuation */}
      <div className="gdh-row1" aria-label="Today's Market and Business Valuation" role="region">
        {/* Today's Market */}
        <section aria-label="Today's Market" data-testid="header-market-section" className="gdh-market">
          <span className="gdh-section-label">Today's Market</span>
          <div className="gdh-market-cards" data-testid="market-cards-container">
            {tickers.length > 0 ? tickers.map((t) => {
              const positive = t.change_pct >= 0;
              return (
                <div key={t.symbol} data-testid={`market-card-${t.symbol.replace(/\s/g, "-")}`} className="gdh-ticker">
                  <div className="gdh-ticker-top">
                    <span className="gdh-ticker-symbol">{t.symbol}</span>
                    <span className="gdh-ticker-pct" style={{ color: positive ? "var(--color-positive)" : "var(--color-negative)" }}>
                      {positive ? "+" : ""}{t.change_pct}%
                    </span>
                  </div>
                  <div className="gdh-ticker-bottom">
                    <span className="gdh-ticker-arrow" aria-label={positive ? "Up" : "Down"} style={{ color: positive ? "var(--color-positive)" : "var(--color-negative)" }}>
                      {positive ? "\u25B2" : "\u25BC"}
                    </span>
                    <span className="gdh-ticker-price" style={{ color: positive ? "var(--color-positive)" : "var(--color-negative)" }}>
                      {t.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <span className="gdh-ticker-change">{positive ? "+" : ""}{t.change.toFixed(2)}</span>
                </div>
              );
            }) : (
              <span className="gdh-placeholder">Loading market data...</span>
            )}
          </div>
        </section>

        {/* Divider */}
        <div className="gdh-divider" aria-hidden="true" />

        {/* Business Valuation */}
        <section aria-label="Business Valuation" data-testid="header-valuation-section" className="gdh-valuation">
          <div className="gdh-valuation-card">
            <div className="gdh-valuation-top">
              <div className="gdh-valuation-label-row">
                <span className="gdh-valuation-label">Business Valuation</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowDisclaimer(true); }}
                  data-testid="valuation-disclaimer-btn"
                  aria-label="Show business valuation disclaimer"
                  className="gdh-valuation-icon-btn"
                >
                  <Info className="gdh-valuation-icon" />
                </button>
              </div>
              <div className="gdh-valuation-badge">
                <TrendingUp className="gdh-valuation-badge-icon" />
                <span>{healthScore}%</span>
              </div>
            </div>
            <p className="gdh-valuation-method">{valuation?.industry || "Revenue Multiple"} - Composite</p>
            <p className="gdh-valuation-amount" data-testid="valuation-amount">{fmtCurrency(valuation?.composite_valuation)}</p>
          </div>
        </section>
      </div>

      {/* Disclaimer modal (rendered inline so click on (i) opens a small popover) */}
      {showDisclaimer && (
        <div
          className="gdh-disclaimer-backdrop"
          data-testid="valuation-disclaimer-backdrop"
          onClick={() => setShowDisclaimer(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="gdh-disclaimer-title"
            data-testid="valuation-disclaimer-dialog"
            className="gdh-disclaimer-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gdh-disclaimer-header">
              <span id="gdh-disclaimer-title" className="gdh-disclaimer-title">
                Business Valuation Disclaimer
              </span>
              <button
                type="button"
                onClick={() => setShowDisclaimer(false)}
                data-testid="valuation-disclaimer-close-btn"
                aria-label="Close disclaimer"
                className="gdh-disclaimer-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="gdh-disclaimer-body">
              {VALUATION_DISCLAIMER_PARAGRAPHS.map((p, idx) => (
                <p key={idx} className="gdh-disclaimer-paragraph">{p}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ROW 2: News (full width, infinite marquee) */}
      <div className="gdh-row2" aria-label="News Headlines" role="region" data-testid="header-news-section">
        <div className="gdh-news-scroll">
          {news.length > 0 ? (
            <div
              className="gdh-news-track"
              data-testid="header-news-track"
              style={{ "--gdh-news-count": Math.min(news.length, 8) }}
            >
              {[0, 1].map((loopIdx) => (
                news.slice(0, 8).map((n, i) => (
                  <a
                    key={`${loopIdx}-${i}`}
                    href={n.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={loopIdx === 0 ? `news-headline-${i}` : undefined}
                    aria-hidden={loopIdx === 1 ? "true" : undefined}
                    tabIndex={loopIdx === 1 ? -1 : 0}
                    className="gdh-news-item"
                    aria-label={loopIdx === 0 ? `${n.source || "News"}: ${(n.title || "").slice(0, 60)}` : undefined}
                  >
                    {n.image_url ? (
                      <img src={n.image_url} alt="" className="gdh-news-thumb" />
                    ) : (
                      <img src={`https://picsum.photos/seed/news-${i}/88/88`} alt="" className="gdh-news-thumb" />
                    )}
                    <div className="gdh-news-text">
                      <span className="gdh-news-title">
                        {(n.title || "").length > 50 ? n.title.slice(0, 50) + ".." : n.title}
                      </span>
                      <span className="gdh-news-source">{n.source || "Business Insider"}</span>
                      <span className="gdh-news-desc">
                        {(n.snippet || n.description || "Latest market commentary").slice(0, 60)}
                        {(n.snippet || n.description || "").length > 60 ? "..." : ""}
                      </span>
                    </div>
                  </a>
                ))
              ))}
            </div>
          ) : (
            <span className="gdh-placeholder">Loading news...</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalDashboardHeader;
